// fetch-with-auth.js
import { API_BASE_URL } from '@config';
import monitoringService from '@services/monitoring';
import { loginRequest } from '@config/auth'; // Import scopes
import { debug } from '@utils/debug'; // Import debug utility

/**
 * Custom error class specifically for errors originating from the fetchWithAuth utility.
 * @extends Error
 */
class FetchError extends Error {
  /**
   * Creates an instance of FetchError.
   * @param {string} message - The error message.
   * @param {string} type - A specific error type code (e.g., 'auth_required', 'token_acquisition_failed', 'cors_error', 'api_error').
   * @param {number} status - The HTTP status code associated with the error (or 0 for network errors).
   */
  constructor(message, type, status) {
    super(message);
    this.name = 'FetchError';
    this.type = type; // e.g., 'auth_required', 'token_acquisition_failed', 'cors_error', 'api_error', 'stream_incomplete'
    this.status = status; // HTTP status code or 0
  }
}

/**
 * Checks if an error message indicates a potential CORS issue.
 * @param {Error} error - The error object.
 * @returns {boolean} True if the error message suggests a CORS problem.
 * @private
 */
const isCorsError = (error) => {
  return (
    error.message.includes('CORS') ||
    error.message.includes('Cross-Origin') ||
    error.message.includes('Access-Control-Allow-Origin')
  );
};

// Request retry configuration
const RETRY_COUNT = 3;
const RETRY_DELAY = 1000; // Base delay in ms

/**
 * Simple promise-based delay function.
 * @param {number} ms - Milliseconds to wait.
 * @returns {Promise<void>} A promise that resolves after the specified delay.
 * @private
 */
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Enhanced fetch wrapper that handles B2C authentication token acquisition,
 * monitoring, retries for specific errors, and Server-Sent Events (SSE) streaming.
 * Intended to be called by the `useApi` hook, not directly by components.
 *
 * @async
 * @function fetchWithAuth
 * @param {string} url - The full URL for the API request.
 * @param {object} [options={}] - Standard fetch options, plus an optional `onProgress` callback for SSE.
 * @param {function} [options.onProgress] - Callback function for handling SSE progress updates. Receives { type: string, payload: any }.
 * @param {import('@azure/msal-browser').IPublicClientApplication} instance - The MSAL instance.
 * @param {Array<import('@azure/msal-browser').AccountInfo>} accounts - Array of authenticated MSAL accounts.
 * @param {number} [retryCount=0] - Internal counter for retry attempts (for 5xx errors and network errors).
 * @returns {Promise<any>} A promise resolving with the API response data (JSON or final SSE payload).
 * @throws {FetchError} Throws a FetchError for various failure conditions (auth, token, CORS, API errors, stream issues).
 */
async function fetchWithAuth(url, options = {}, instance, accounts, retryCount = 0) {
  const startTime = Date.now();
  const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

  // Track API call start
  monitoringService.trackEvent('ApiCallStarted', {
    url: url.replace(API_BASE_URL, ''),
    method: options.method || 'GET',
    requestId,
    retryCount
  });

  try {
    // Verify auth context is valid
    if (!instance || !accounts || accounts.length === 0) {
      throw new FetchError(
        'Authentication required',
        'auth_required',
        401
      );
    }
    const account = accounts[0]; // Use the first account

    // Get access token from auth context
    let accessToken;
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest, // Use scopes from config
        account: account
      });
      accessToken = tokenResponse.accessToken;
    } catch (error) {
      // Handle interaction required errors if necessary, though ideally useApi prevents calls during interaction
      debug('fetch:auth:error', 'Token acquisition failed:', error);
      // Re-throw as a FetchError for consistent handling upstream
      throw new FetchError('Failed to acquire authentication token', 'token_acquisition_failed', 401);
    }

    if (!accessToken) {
      throw new FetchError(
        'No authentication token available',
        'token_missing',
        401
      );
    }

    // Don't set any default headers if we're sending FormData
    const isFormData = options.body instanceof FormData;

    const defaultOptions = {
      headers: isFormData ? {} : {
        'Content-Type': 'application/json',
      },
    };

    // Add auth header
    Object.assign(defaultOptions.headers, {
      'Authorization': `Bearer ${accessToken}`,
      'X-Request-ID': requestId
    });

    // For FormData, don't override Content-Type
    const requestOptions = isFormData ? {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers
      }
    } : {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      }
    };

    // Track dependency call
    const dependencyStartTime = Date.now();
    const response = await fetch(url, requestOptions);
    const dependencyDuration = Date.now() - dependencyStartTime;

    // Track dependency duration
    monitoringService.trackMetric('ApiDependencyDuration', dependencyDuration, {
      url: url.replace(API_BASE_URL, ''),
      method: options.method || 'GET'
    });

    // Check for correlation ID
    const correlationId = response.headers.get('x-request-id');
    if (correlationId) {
      monitoringService.setOperationId(correlationId);
    }

    // Handle errors first
    if (!response.ok) {
      // Removed logging
      const errorData = await response.text();
      let parsedError;
      try {
        parsedError = JSON.parse(errorData);
      } catch {
        parsedError = { message: errorData };
      }

      // Check for CORS errors
      if (response.status === 0 || response.type === 'opaque' ||
          (response.status === 403 && parsedError.message?.includes('CORS'))) {
        debug('fetch:error', 'Possible CORS error:', { status: response.status, type: response.type, url, error: parsedError });
        throw new FetchError('CORS error: Unable to access the API', 'cors_error', 403);
      }

      // Handle auth errors
      if (response.status === 401) {
        debug('fetch:error', 'API returned 401 Unauthorized.');
        throw new FetchError('API request unauthorized', 'auth_failed', 401);
      }

      // For other server errors, attempt retry if within limits
      if (retryCount < RETRY_COUNT && response.status >= 500) {
        await wait(RETRY_DELAY * Math.pow(2, retryCount));
        return fetchWithAuth(url, options, instance, accounts, retryCount + 1);
      }

      // Throw specific API error for non-retried failures
      throw new FetchError(
        parsedError.message || `API request failed: ${response.statusText}`,
        'api_error',
        response.status
      );
    }

    // Handle successful response (response.ok is true)
    if (options.onProgress && typeof options.onProgress === 'function' && response.body) {
      // --- Streaming Response Handling ---
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = 'message'; // Default event type
      let finalData = null;
      // Removed logging
      // Removed counter
      while (true) {
        // Removed counter
        // Removed logging
        const { value, done } = await reader.read();
        // Removed logging
        if (done) {
          // Removed logging
          if (buffer.trim()) {
             debug('fetch:sse:warn', 'Stream ended with unprocessed buffer:', buffer); // Kept this warning
          }
          break; // Exit loop when stream ends
        }

        buffer += decoder.decode(value, { stream: true });
        // Removed logging
        // Removed logging
        // Removed logging
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine === '') continue; // Skip empty lines

          // Check for SSE 'event:' line first
          if (trimmedLine.startsWith('event:')) {
            currentEvent = trimmedLine.substring(6).trim();
          }
          // Check for SSE 'data:' line
          else if (trimmedLine.startsWith('data:')) {
            const jsonData = trimmedLine.substring(5).trim();
            if (jsonData) {
              try {
                const data = JSON.parse(jsonData);

                // Handle based on the currentEvent type
                switch (currentEvent) {
                  case 'strategy_info':
                    debug('fetch:sse', 'Received strategy_info:', data);
                    // Pass strategy info via onProgress
                    // Removed logging
                    options.onProgress({ type: 'strategy_info', payload: data });
                    break;
                  case 'final_data':
                    debug('fetch:sse', 'Received final_data:', data);
                    finalData = data; // Store final data payload
                    // Don't call onProgress here, wait for stream to end
                    break;
                  case 'error':
                     debug('fetch:sse:error', 'Received error event from backend:', data);
                     // Propagate error via onProgress or throw? Let's use onProgress for now.
                     // Removed logging
                     options.onProgress({ type: 'error', payload: data });
                     // We might want to close the reader here depending on desired behavior
                     // reader.cancel(); // Optional: Cancel further reading on error
                     break;
                  case 'message': // Default event for progress stages
                  default:
                    if (data.stage) {
                       // Pass stage update via onProgress
                       // Removed logging
                       options.onProgress({ type: 'stage', payload: data.stage });
                    } else {
                       debug('fetch:sse:warn', "Received message event without 'stage' property:", data);
                    }
                    break;
                }
              } catch (e) {
                debug('fetch:sse:error', 'Error parsing SSE data line:', jsonData, e);
              }
            }
            // Reset event type after processing data line for it
            currentEvent = 'message';
          } else if (trimmedLine !== '') {
             debug('fetch:sse:warn', 'Received non-empty, non-SSE line in stream:', trimmedLine);
          }
        }
      } // End while loop
      // Removed logging

      if (finalData !== null) { // Check if finalData was received
        // Removed logging
        // Track successful streaming API call
        const duration = Date.now() - startTime;
        monitoringService.trackEvent('ApiCallCompleted', { url: url.replace(API_BASE_URL, ''), method: options.method || 'GET', statusCode: response.status, duration, requestId, retryCount });
        monitoringService.trackMetric('ApiCallDuration', duration, { url: url.replace(API_BASE_URL, ''), method: options.method || 'GET', statusCode: response.status, success: true, retryCount });
        return finalData; // Return the assembled final data
      } else {
        // Removed logging
        // Throw error if stream ended without the final result message
        throw new FetchError('Stream completed without sending final result data', 'stream_incomplete', 500);
      }
    } else {
      // --- Standard Non-Streaming JSON Response Handling ---
      const data = await response.json();
      // Track successful non-streaming API call
      const duration = Date.now() - startTime;
      monitoringService.trackEvent('ApiCallCompleted', { url: url.replace(API_BASE_URL, ''), method: options.method || 'GET', statusCode: response.status, duration, requestId, retryCount });
      monitoringService.trackMetric('ApiCallDuration', duration, { url: url.replace(API_BASE_URL, ''), method: options.method || 'GET', statusCode: response.status, success: true, retryCount });
      return data; // Return the parsed JSON data
    }
    // End of successful response handling
  } catch (error) {
    // Removed logging
    // Track API call error
    const duration = Date.now() - startTime;
    const errorDetails = {
      url: url.replace(API_BASE_URL, ''),
      method: options.method || 'GET',
      duration,
      requestId,
      retryCount,
      isCorsError: isCorsError(error)
    };

    monitoringService.trackException(error, errorDetails);

    monitoringService.trackMetric('ApiCallDuration', duration, {
      ...errorDetails,
      success: false,
      errorType: error.name,
      errorMessage: error.message
    });

    // Only retry on network errors, not token acquisition failures or other FetchErrors
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch') && retryCount < RETRY_COUNT) {
      monitoringService.trackEvent('ApiRetryAttempt', {
        requestId,
        reason: 'network_error',
        retryCount
      });
      await wait(RETRY_DELAY * Math.pow(2, retryCount));
      // Corrected retry call:
      return fetchWithAuth(url, options, instance, accounts, retryCount + 1);
    }

    // Enhance error tracking with specific error types
    if (error instanceof FetchError) {
      throw error;
    }

    throw new FetchError(
      error.message,
      'unknown_error',
      500
    );
  }
}

export default fetchWithAuth;
